import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useSendCode } from '@/hooks/useAuth';
import { extractApiError } from '@/api/client';

/**
 * Tenant invite landing page.
 *
 * Target of the deep link in the invite SMS: SendTenantInviteJob builds
 * `${config('monthly.web_url')}/invite?phone=...`. The phone is pre-filled
 * from the query string; submitting kicks off the standard OTP login flow,
 * after which a new user lands on /onboarding.
 */
export function InvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // useSearchParams already URL-decodes, so `%2B7701...` arrives as `+7701...`.
  const phoneParam = searchParams.get('phone')?.trim();
  const [phone, setPhone] = useState(phoneParam && phoneParam.length > 1 ? phoneParam : '+7');
  const [error, setError] = useState<string | null>(null);
  const sendCode = useSendCode();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await sendCode.mutateAsync(phone);
      navigate('/login/otp', { state: { phone } });
    } catch (err) {
      setError(extractApiError(err).message);
    }
  };

  return (
    <Card title={t('auth.invite_title')}>
      <p className="mb-4 text-sm text-slate-600">{t('auth.invite_subtitle')}</p>
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label={t('auth.phone_label')}
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t('auth.phone_placeholder')}
          autoFocus
          required
          error={error ?? undefined}
        />
        <Button type="submit" className="w-full" loading={sendCode.isPending}>
          {t('auth.send_code')}
        </Button>
      </form>
    </Card>
  );
}
